import { BookOpen, X } from "lucide-react";
import {
  GUITAR_BASICS_REFERENCE_TIPS,
  OPEN_STRINGS_MNEMONIC,
  OPEN_STRINGS_ORDER_SHORT,
  OPEN_STRINGS_REFERENCE,
} from "../../lib/gym/guitarBasicsReference";
import { BEGINNER_PATH_THEME as PATH } from "../../lib/beginnerPath/beginnerPathTheme";
import { GYM_STUDIO } from "../../lib/gym/gymStudioTheme";

export interface GuitarBasicsReferenceSheetProps {
  open: boolean;
  onClose: () => void;
  pathMode?: boolean;
}

/**
 * Panel ligero: orden de cuerdas + mnemotécnica. No navega fuera del camino.
 */
export function GuitarBasicsReferenceSheet({
  open,
  onClose,
  pathMode = false,
}: GuitarBasicsReferenceSheetProps) {
  if (!open) return null;

  const accent = pathMode ? "#99f6e4" : GYM_STUDIO.accent;
  const accentSoft = pathMode ? "rgba(153, 246, 228, 0.12)" : GYM_STUDIO.accentSoft;
  const border = pathMode ? PATH.cardBorderMint : GYM_STUDIO.borderHover;
  const textMain = pathMode ? PATH.text : GYM_STUDIO.textMain;
  const textMuted = pathMode ? PATH.textMuted : GYM_STUDIO.textMuted;
  const textDim = pathMode ? PATH.textDim : GYM_STUDIO.textDim;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-black/65"
        aria-label="Cerrar referencia"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-labelledby="guitar-basics-ref-title"
        className="relative z-10 w-full sm:max-w-xl max-h-[min(92vh,640px)] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5 sm:p-7 shadow-2xl"
        style={{
          background: "linear-gradient(165deg, rgba(42,38,36,0.98) 0%, rgba(22,20,19,0.99) 100%)",
          border: `1px solid ${border}`,
          boxShadow: `0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px ${accentSoft}`,
        }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <p
              className="text-[10px] uppercase tracking-[0.14em] mb-1"
              style={{ color: accent }}
            >
              Referencia rápida
            </p>
            <h2
              id="guitar-basics-ref-title"
              className="font-serif text-xl font-light"
              style={{ color: textMain }}
            >
              Orden de las cuerdas
            </h2>
            <p className="text-xs mt-1 tabular-nums" style={{ color: textDim }}>
              {OPEN_STRINGS_ORDER_SHORT}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/5"
            style={{ border: `1px solid ${pathMode ? PATH.cardBorder : GYM_STUDIO.border}`, color: textMuted }}
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <ol className="space-y-1.5 mb-4">
          {OPEN_STRINGS_REFERENCE.map((s) => (
            <li
              key={s.string}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${pathMode ? PATH.cardBorder : GYM_STUDIO.border}`,
              }}
            >
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                style={{ background: accentSoft, color: accent }}
              >
                {s.string}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium" style={{ color: textMain }}>
                  {s.note}{" "}
                  <span className="font-normal tabular-nums" style={{ color: textDim }}>
                    ({s.midiLabel})
                  </span>
                </p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: textDim }}>
                  {s.role === "grave" ? "Grave" : s.role === "media" ? "Media" : "Aguda"} · al aire
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div
          className="rounded-xl px-3 py-3 mb-4 text-xs leading-relaxed"
          style={{ background: accentSoft, color: accent, border: `1px solid ${accent}33` }}
        >
          <p className="font-semibold mb-1 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" />
            Mnemotécnica
          </p>
          <p style={{ color: pathMode ? "rgba(153,246,228,0.9)" : GYM_STUDIO.accent }}>
            {OPEN_STRINGS_MNEMONIC}
          </p>
        </div>

        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: textDim }}>
          Recuerda
        </p>
        <ul className="space-y-1.5 mb-5">
          {GUITAR_BASICS_REFERENCE_TIPS.map((tip) => (
            <li key={tip} className="text-xs leading-relaxed pl-3 border-l" style={{ color: textMuted, borderColor: accentSoft }}>
              {tip}
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={onClose}
          className="w-full h-11 rounded-xl text-sm font-semibold text-[#100f0e]"
          style={{ background: pathMode ? PATH.btnMint : GYM_STUDIO.accent }}
        >
          Entendido
        </button>
      </aside>
    </div>
  );
}

export function GuitarBasicsReferenceTrigger({
  onClick,
  pathMode = false,
  compact = false,
}: {
  onClick: () => void;
  pathMode?: boolean;
  compact?: boolean;
}) {
  const accent = pathMode ? "#99f6e4" : GYM_STUDIO.accent;
  const border = pathMode ? PATH.cardBorder : GYM_STUDIO.border;
  const bg = pathMode ? PATH.cardBg : GYM_STUDIO.bgElevated;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-xl border transition-colors hover:bg-white/5 ${
        compact ? "px-2.5 py-1.5 text-[10px]" : "px-3 py-2 text-xs"
      } font-medium`}
      style={{ borderColor: border, background: bg, color: accent }}
      title="Orden de las cuerdas"
    >
      <BookOpen className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
      Referencia
    </button>
  );
}

export default GuitarBasicsReferenceSheet;
